import { useCallback, useState } from 'react';
import { Link2 } from 'lucide-react';
import { useMarkInvitedMutation, useMeQuery } from '../profiles/api';
import { inviteLinksApi, useRenewInviteLinkMutation, type InviteLinkView } from './api';
import { inviteShareText, withVia } from '../onboarding/share';
import { track } from '../onboarding/track';

/**
 * "Share an invite link" — beside the email invite in the new-conversation
 * panel. The link is only made when the member asks for it, then handed to the
 * phone's share sheet (WhatsApp, Messages…) or copied where there is none.
 */
export function ShareInviteLink() {
  const { data: me } = useMeQuery();
  const [fetchLink, { isFetching }] = inviteLinksApi.useLazyMyInviteLinkQuery();
  const [renew, { isLoading: renewing }] = useRenewInviteLinkMutation();
  const [markInvited] = useMarkInvitedMutation();
  const [link, setLink] = useState<InviteLinkView | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const hand = useCallback(
    async (view: InviteLinkView) => {
      setLink(view);
      // The link rides inside the text, so every app shows the same line.
      const handle = me?.handle ?? 'me';
      if (typeof navigator.share === 'function') {
        const text = inviteShareText(handle, withVia(view.url, 'sheet'));
        track({ event: 'share_opened', channel: 'sheet' });
        try {
          await navigator.share({ title: 'toiyp', text });
          void markInvited();
          setMsg(null);
          return;
        } catch (e) {
          // Closing the share sheet is a choice, not an error.
          if ((e as { name?: string }).name === 'AbortError') return;
        }
      }
      track({ event: 'share_opened', channel: 'copy' });
      const text = inviteShareText(handle, withVia(view.url, 'copy'));
      try {
        await navigator.clipboard.writeText(text);
        void markInvited();
        setMsg('Link copied. Paste it wherever you talk to them.');
      } catch {
        setMsg('Copy the link above and send it to them.');
      }
    },
    [me?.handle, markInvited],
  );

  const onShare = useCallback(async () => {
    setErr(null);
    try {
      await hand(await fetchLink().unwrap());
    } catch {
      setErr('Could not make a link just now.');
    }
  }, [fetchLink, hand]);

  const onRenew = useCallback(async () => {
    setErr(null);
    setMsg(null);
    try {
      setLink(await renew().unwrap());
      setMsg('The old link is closed. This one is new.');
    } catch {
      setErr('Could not make a new link just now.');
    }
  }, [renew]);

  return (
    <div className="new-conv__invite">
      <span className="new-conv__invite-title">Or share an invite link</span>
      <button
        type="button"
        className="btn btn--ghost new-conv__invite-send"
        disabled={isFetching}
        onClick={() => void onShare()}
      >
        <Link2 size={15} aria-hidden="true" />
        {isFetching ? 'Making a link…' : 'Share a link'}
      </button>
      {link && (
        <>
          <input
            className="field__input invite-link__url"
            value={link.url}
            readOnly
            aria-label="Your invite link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <p className="field__hint invite-link__hint">
            Open until{' '}
            {new Date(link.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} and
            for {link.usesLeft} more{' '}
            {link.usesLeft === 1 ? 'person' : 'people'}. Whoever opens it finds a blank page
            addressed to you.{' '}
            <button type="button" className="linkish" disabled={renewing} onClick={() => void onRenew()}>
              Close it and make a new one
            </button>
          </p>
        </>
      )}
      {msg && (
        <p className="invite-friend__ok" role="status">
          {msg}
        </p>
      )}
      {err && (
        <p className="form-error invite-friend__err" role="alert">
          {err}
        </p>
      )}
    </div>
  );
}
